/**
 * ADA Fix: Focus Outline Contrast Enforcement
 * 
 * Issue: Some focus indicators use light colors like #D1DBE2 on white backgrounds,
 * resulting in insufficient contrast (1.4:1) when WCAG 2.2 requires minimum 3:1.
 * 
 * Solution: Dynamically detect and replace low-contrast focus outlines with
 * properly contrasted alternatives (#767676 provides 4.5:1 on white).
 * 
 * WCAG 2.2 Criterion: 1.4.11 Non-text Contrast (Level AA)
 * Minimum contrast ratio: 3:1 for UI components
 */

(function() {
  'use strict';

  console.log('🔧 ADA Fix: Focus outline contrast enforcement starting...');

  // Colors with insufficient contrast on white background
  const LOW_CONTRAST_COLORS = [
    '#D1DBE2',
    '#d1dbe2',
    'rgb(209, 219, 226)',
    'rgb(209,219,226)',
    'rgba(209, 219, 226',
    'rgba(209,219,226'
  ];

  // Replacement color with 4.5:1 contrast on white
  const HIGH_CONTRAST_COLOR = '#767676';

  /**
   * Calculate relative luminance of a color
   * @param {number} r - Red value (0-255)
   * @param {number} g - Green value (0-255)
   * @param {number} b - Blue value (0-255)
   * @returns {number} - Relative luminance
   */
  function getLuminance(r, g, b) {
    const [rs, gs, bs] = [r, g, b].map(val => {
      val = val / 255;
      return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  }

  /**
   * Calculate contrast ratio between two colors
   * @param {string} color1 - First color
   * @param {string} color2 - Second color
   * @returns {number} - Contrast ratio
   */
  function getContrastRatio(color1, color2) {
    const lum1 = getColorLuminance(color1);
    const lum2 = getColorLuminance(color2);
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    return (lighter + 0.05) / (darker + 0.05);
  }

  /**
   * Get luminance from color string
   * @param {string} color - Color string (hex, rgb, rgba)
   * @returns {number} - Luminance value
   */
  function getColorLuminance(color) {
    // Convert to RGB
    let r, g, b;

    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      r = parseInt(hex.substr(0, 2), 16);
      g = parseInt(hex.substr(2, 2), 16);
      b = parseInt(hex.substr(4, 2), 16);
    } else if (color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match) {
        [r, g, b] = match.map(Number);
      }
    } else {
      return 0;
    }

    return getLuminance(r, g, b);
  }

  /**
   * Check if outline color has low contrast
   * @param {string} outlineColor - The outline color to check
   * @returns {boolean} - True if contrast is insufficient
   */
  function hasLowContrast(outlineColor) {
    if (!outlineColor || outlineColor === 'none') return false;

    // Direct match with known low-contrast colors
    const normalizedColor = outlineColor.toLowerCase().replace(/\s/g, '');
    for (const lowColor of LOW_CONTRAST_COLORS) {
      if (normalizedColor.includes(lowColor.toLowerCase().replace(/\s/g, ''))) {
        return true;
      }
    }

    // Calculate contrast ratio against white
    const ratio = getContrastRatio(outlineColor, '#FFFFFF');
    
    // Return true if ratio is less than 3:1
    return ratio < 3;
  }

  /**
   * Fix outline color for an element
   * @param {HTMLElement} element - Element to fix
   */
  function fixOutlineColor(element) {
    const computedStyle = window.getComputedStyle(element);
    const outlineColor = computedStyle.outlineColor;

    if (hasLowContrast(outlineColor)) {
      console.log(`⚠️  Low contrast outline detected: ${outlineColor}`);
      console.log(`✅ Replacing with: ${HIGH_CONTRAST_COLOR}`);
      
      element.style.setProperty('outline-color', HIGH_CONTRAST_COLOR, 'important');
      
      // Ensure outline is visible
      if (computedStyle.outlineStyle === 'none') {
        element.style.setProperty('outline-style', 'solid', 'important');
      }
      if (parseFloat(computedStyle.outlineWidth) < 2) {
        element.style.setProperty('outline-width', '3px', 'important');
      }

      return true;
    }

    return false;
  }

  /**
   * Check and fix focus outlines on focus event
   */
  function setupFocusListener() {
    let lastFocusedElement = null;

    document.addEventListener('focus', function(event) {
      const element = event.target;
      
      // Avoid processing same element multiple times
      if (element === lastFocusedElement) return;
      lastFocusedElement = element;

      // Small delay to allow styles to be computed
      setTimeout(function() {
        fixOutlineColor(element);
      }, 0);
    }, true); // Use capture phase

    console.log('✅ Focus listener installed');
  }

  /**
   * Scan and fix all currently focused elements
   */
  function scanExistingElements() {
    const focusedElement = document.activeElement;
    
    if (focusedElement && focusedElement !== document.body) {
      fixOutlineColor(focusedElement);
    }
  }

  /**
   * Override style setters to catch inline styles
   */
  function setupStyleInterception() {
    const originalSetAttribute = Element.prototype.setAttribute;
    
    Element.prototype.setAttribute = function(name, value) {
      originalSetAttribute.call(this, name, value);
      
      if (name === 'style' && value.includes('outline')) {
        setTimeout(() => {
          if (this === document.activeElement) {
            fixOutlineColor(this);
          }
        }, 0);
      }
    };

    console.log('✅ Style interception installed');
  }

  /**
   * Setup mutation observer for dynamic content
   */
  function setupMutationObserver() {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
          const element = mutation.target;
          if (element === document.activeElement) {
            setTimeout(function() {
              fixOutlineColor(element);
            }, 0);
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      subtree: true
    });

    console.log('✅ Mutation observer installed');
    return observer;
  }

  /**
   * Initialize all fixes
   */
  function initialize() {
    console.log('🚀 Initializing focus outline contrast fixes...');

    // Setup listeners and observers
    setupFocusListener();
    setupStyleInterception();
    setupMutationObserver();

    // Scan existing elements
    scanExistingElements();

    console.log('✅ Focus outline contrast enforcement initialized');
    console.log(`📊 Target colors to replace: ${LOW_CONTRAST_COLORS.join(', ')}`);
    console.log(`📊 Replacement color: ${HIGH_CONTRAST_COLOR} (4.5:1 on white)`);
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Expose for testing
  window.adaFocusOutlineContrastFix = {
    fixOutlineColor,
    hasLowContrast,
    getContrastRatio,
    LOW_CONTRAST_COLORS,
    HIGH_CONTRAST_COLOR
  };

  console.log('🔧 ADA Focus Outline Contrast Fix loaded');

})();
